// src/pages/auth/SignInPage.js
/* Filepath: // src/pages/auth/SignInPage.js */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  signInUserWithEmailPassword,
  getFriendlyErrorMessage,
  startGoogleRedirect,
  startAppleRedirect,
  handleRedirectResultOnce,
} from "../../services/authService";
import {
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  Container,
  Link,
} from "@mui/material";
import SignInForm from "../../components/auth/SignInForm";
import useAuthStore from "../../store/useAuthStore";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import BrandLogo from "../../components/BrandLogo";

const REDIRECT_CTX_KEY = "redirectCtx";
const PAIR_QS_KEY = "__PAIR_QS__";

export default function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchQS = location.search || "";

  const { setUser } = useAuthStore();

  // Persist current QS (if it contains a code) so SignUpPage can recover it
  useEffect(() => {
    const hasCode = new URLSearchParams(searchQS).has("code");
    if (hasCode) {
      try {
        sessionStorage.setItem(PAIR_QS_KEY, searchQS);
      } catch {}
    }
  }, [searchQS]);

  const storedPairQS = (() => {
    try {
      return sessionStorage.getItem(PAIR_QS_KEY) || "";
    } catch {
      return "";
    }
  })();

  // Parse current code if present
  const params = new URLSearchParams(searchQS);
  const codeFromUrl = (params.get("code") || "").toUpperCase().trim();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authedUser, setAuthedUser] = useState(null);
  const [switching, setSwitching] = useState(false);

  // Track auth state
  useEffect(() => {
    const auth = getAuth();
    setAuthedUser(auth.currentUser || null);
    const unsub = onAuthStateChanged(auth, (u) => setAuthedUser(u || null));
    return unsub;
  }, []);

  // Only redirect AFTER a sign-in that happens while this page is mounted.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (!authedUser) return;

    const currentHasCode = new URLSearchParams(searchQS).has("code");
    const storedHasCode = new URLSearchParams(storedPairQS || "").has("code");
    const finalQS = currentHasCode ? searchQS : storedHasCode ? storedPairQS : "";

    if (finalQS && new URLSearchParams(finalQS).has("code")) {
      navigate(`/pair${finalQS}`, { replace: true });
    } else {
      navigate("/interact", { replace: true });
    }
  }, [authedUser, searchQS, storedPairQS, navigate]);

  // Handle OAuth redirect completion — recover searchQS if lost
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let savedCtx = {};
      try {
        savedCtx = JSON.parse(sessionStorage.getItem(REDIRECT_CTX_KEY) || "{}");
      } catch {
        savedCtx = {};
      }
      if (!savedCtx?.pairingCode && !savedCtx?.searchQS) return;

      setLoading(true);
      try {
        const user = await handleRedirectResultOnce(
          savedCtx.pairingCode ?? codeFromUrl,
          false
        );
        if (cancelled) return;

        if (user) {
          setUser(user);
          setAuthedUser(user);

          // Choose the best available QS to preserve pairing code
          const currentHasCode = new URLSearchParams(searchQS).has("code");
          const savedHasCode = new URLSearchParams(savedCtx.searchQS || "").has("code");
          const storedHasCode = new URLSearchParams(storedPairQS || "").has("code");

          let finalQS = "";
          if (currentHasCode) finalQS = searchQS;
          else if (savedHasCode) finalQS = savedCtx.searchQS;
          else if (storedHasCode) finalQS = storedPairQS;
          else if (savedCtx.pairingCode) finalQS = `?code=${encodeURIComponent(String(savedCtx.pairingCode).toUpperCase().trim())}`;

          if (finalQS && new URLSearchParams(finalQS).has("code")) {
            try { sessionStorage.setItem(PAIR_QS_KEY, finalQS); } catch {}
            navigate(`/pair${finalQS}`, { replace: true });
          } else {
            navigate("/interact", { replace: true });
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Redirect result handling failed:", e);
        if (!cancelled) setError("Error finalizing redirect sign-in. Please try again.");
      } finally {
        sessionStorage.removeItem(REDIRECT_CTX_KEY);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeFromUrl, searchQS, storedPairQS]);

  // ---------- Button handlers ----------
  const handleEmailPasswordSignIn = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const user = await signInUserWithEmailPassword(
        email,
        password,
        codeFromUrl,
        false
      );
      setUser(user);
      setAuthedUser(user);
      // Post-mount watcher will redirect using current or stored QS
    } catch (err) {
      const friendlyMessage = getFriendlyErrorMessage(err.code);
      setError(friendlyMessage);
      // eslint-disable-next-line no-console
      console.error("Error signing in with email and password:", err?.message || err);
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = await startGoogleRedirect(codeFromUrl, false);
      if (user) {
        setUser(user);
        setAuthedUser(user);
        // watcher will navigate using current/stored QS
      } else {
        // Persist pairing context for the redirect round-trip
        const currentHasCode = new URLSearchParams(searchQS).has("code");
        const qsToSave = currentHasCode
          ? searchQS
          : codeFromUrl
          ? `?code=${encodeURIComponent(codeFromUrl)}`
          : "";
        if (qsToSave) {
          try { sessionStorage.setItem(PAIR_QS_KEY, qsToSave); } catch {}
        }
        sessionStorage.setItem(
          REDIRECT_CTX_KEY,
          JSON.stringify({
            pairingCode: codeFromUrl,
            searchQS, // preserve full query string for return
          })
        );
        return; // actual redirect happens by the provider
      }
    } catch (err) {
      const friendlyMessage = getFriendlyErrorMessage(err.code);
      setError(friendlyMessage);
      // eslint-disable-next-line no-console
      console.error("Error starting Google sign-in:", err?.message || err);
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = await startAppleRedirect(codeFromUrl, false);
      if (user) {
        setUser(user);
        setAuthedUser(user);
      } else {
        const currentHasCode = new URLSearchParams(searchQS).has("code");
        const qsToSave = currentHasCode
          ? searchQS
          : codeFromUrl
          ? `?code=${encodeURIComponent(codeFromUrl)}`
          : "";
        if (qsToSave) {
          try { sessionStorage.setItem(PAIR_QS_KEY, qsToSave); } catch {}
        }
        sessionStorage.setItem(
          REDIRECT_CTX_KEY,
          JSON.stringify({
            pairingCode: codeFromUrl,
            searchQS,
          })
        );
        return;
      }
    } catch (err) {
      const friendlyMessage = getFriendlyErrorMessage(err.code);
      setError(friendlyMessage);
      // eslint-disable-next-line no-console
      console.error("Error starting Apple sign-in:", err?.message || err);
      setLoading(false);
    }
  };

  // Safe sign-out (stay on /signin and show the form; keep query string)
  const handleSignInAsDifferent = async (e) => {
    e.preventDefault();
    if (switching) return;
    setSwitching(true);
    try {
      const auth = getAuth();
      try {
        await signOut(auth);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Sign out failed (continuing):", err);
      }
      try { localStorage.clear(); } catch {}
      try { sessionStorage.setItem(PAIR_QS_KEY, searchQS || ""); } catch {}
      try {
        if (indexedDB?.databases) {
          const dbs = await indexedDB.databases();
          await Promise.all(
            dbs.map(
              (db) =>
                db?.name &&
                new Promise((res) => {
                  const req = indexedDB.deleteDatabase(db.name);
                  req.onsuccess = req.onerror = req.onblocked = () => res();
                })
            )
          );
        } else {
          ["firebaseLocalStorageDb", "firebase-installations-database"].forEach((name) => {
            try { indexedDB.deleteDatabase(name); } catch {}
          });
        }
      } catch {}

      navigate(`/signin${searchQS}`, { replace: true });
      setAuthedUser(null);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <>
      <Backdrop sx={{ color: "#fff", zIndex: 100 }} open={loading}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Signing in...
          </Typography>
        </Box>
      </Backdrop>

      <Container
        maxWidth="xs"
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          p: 4,
        }}
      >
        <Box sx={{ width: "100%" }}>
          <BrandLogo size={100} mb={8} />

          {authedUser ? (
            <Typography align="center">
              You’re signed in with {authedUser.email || "your account"}.{" "}
              <Link
                underline="hover"
                onClick={handleSignInAsDifferent}
                sx={{ cursor: switching ? "not-allowed" : "pointer", opacity: switching ? 0.7 : 1 }}
              >
                {switching ? "Signing out…" : "Sign in as a different user"}
              </Link>
            </Typography>
          ) : (
            <SignInForm
              handleEmailPasswordSignIn={handleEmailPasswordSignIn}
              handleGoogleSignIn={handleGoogleSignIn}
              handleAppleSignIn={handleAppleSignIn}
              loading={loading}
              error={error}
            />
          )}
        </Box>
      </Container>
    </>
  );
}
